<template>
  <BittsLayout :is-page="true" layout-classes="overflow-y-auto">
    <template #content>
      <PageTitle title="API" :subtitle="API_PAGE_SUBTITLE">
        <template #action>
          <div class="api-page__actions">
            <BittsButton
              text="API docs"
              type="neutral"
              :right-icon="['fak', 'external-link']"
              @click="handleDocsClick"
            />
            <BittsButton
              :text="currentTab.createCtaText"
              :left-icon="['fak', 'add']"
              @click="handleCreateClick"
            />
          </div>
        </template>
      </PageTitle>
      <BittsTabs
        :current-value="currentTab.value"
        :tabs="tabs"
        :full-width="false"
        class="api-page__tabs"
        data-testid="api-page-tabs"
        @change:tab="openTab"
      >
        <template #labelSuffix="{ value }">
          <BittsCount
            :count="countsByTab[value]"
            :type="value === currentTab.value ? 'default' : 'neutral'"
            :show-zero="true"
            class="ml-4 mt-2"
          />
        </template>
      </BittsTabs>
      <Component
        :is="currentTab.component"
        class="api-page__tab"
        role="tabpanel"
        :aria-label="currentTab.name"
      />
      <router-view-wrapper />
    </template>
  </BittsLayout>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCount,
  BittsLayout,
  BittsTabs,
} from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import PageTitle from '@/components/PageTitle.vue';

import { API_DOCS_URL, API_PAGE_SUBTITLE } from './constants';
import useApiRoutes from './useApiRoutes';
import useApiTabs from './useApiTabs';

useHead({ title: 'API - Crossbeam' });

const route = useRoute();
const { tabs, getTabByValue } = useApiTabs();
const { openTab, openCreate } = useApiRoutes();

const currentTab = computed(() => getTabByValue(route.params.tab as string));

const countsByTab = computed(() =>
  Object.fromEntries(tabs.value.map((tab) => [tab.value, tab.count])),
);

function handleCreateClick() {
  openCreate(currentTab.value.value);
}

function handleDocsClick() {
  window.open(API_DOCS_URL, '_blank', 'noopener');
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.api-page__actions {
  @apply flex items-center gap-12;
}

.api-page__tab {
  @apply flex flex-col gap-16 flex-1 min-h-0;
}

.api-page__tabs {
  @apply mb-24 border-b-2 border-neutral-background-disabled;
}

.api-page__tabs :deep(.bitts-simple-tabs__item) {
  @apply mb-[-2px];
}
</style>
